import { stripRowTokenPrefix } from './reference-token-id';
import { sortReferenceRowsCanonical } from './reference-row-order';

/**
 * Minimal shape needed to index Reference rows. `T` extends this so callers
 * can pass richer row types (e.g. `EditLinkRefRow`) through unchanged.
 */
export type ReferenceRowIndexEntry = {
  id: string;
  sourceId: string;
  refId: string | null;
  createdAt?: string;
};

export type ReferenceRowIndex<T extends ReferenceRowIndexEntry> = {
  /** Keyed by the row's own database id. */
  byId: Map<string, T>;
  /**
   * Keyed by `sourceTokenKey(sourceId, refId)` — legacy target-id tokens,
   * shared by every row citing that target from that block. Holds ALL rows
   * for the pair, in canonical order (see `reference-row-order.ts`) — the
   * first element is what a legacy chip resolves to.
   */
  bySourceAndToken: Map<string, T[]>;
};

/** Stable composite key for a (sourceBlockId, tokenId) pair. */
export function sourceTokenKey(sourceId: string, tokenId: string): string {
  return `${sourceId}::${tokenId}`;
}

/**
 * Builds an O(1)-lookup index from a flat Reference rows array. Call once per
 * `rows` identity change (e.g. in a provider gated on query-data identity),
 * not on every render of every editor — that's the whole point of this
 * module (see `reference-row-index-context.tsx`).
 */
export function buildReferenceRowIndex<T extends ReferenceRowIndexEntry>(
  rows: readonly T[] | undefined,
): ReferenceRowIndex<T> {
  const byId = new Map<string, T>();
  const bySourceAndTokenUnsorted = new Map<string, T[]>();

  for (const row of rows ?? []) {
    byId.set(row.id, row);
    if (row.refId != null) {
      const key = sourceTokenKey(row.sourceId, row.refId);
      const list = bySourceAndTokenUnsorted.get(key) ?? [];
      list.push(row);
      bySourceAndTokenUnsorted.set(key, list);
    }
  }

  const bySourceAndToken = new Map<string, T[]>();
  for (const [key, list] of bySourceAndTokenUnsorted) {
    bySourceAndToken.set(key, sortReferenceRowsCanonical(list));
  }

  return { byId, bySourceAndToken };
}

export type ReferenceRowLookupOptions = {
  /** Which occurrence of a shared legacy token this lookup means (0-based). */
  occurrenceIndex?: number;
  /** Row ids to skip — rows a sibling occurrence has already claimed via its
   *  own row-scoped token (see `collectClaimedRowIds`). */
  excludeRowIds?: ReadonlySet<string>;
};

export type ReferenceRowLookupResult<T extends ReferenceRowIndexEntry> = {
  row: T | undefined;
  /**
   * True when this occurrence has no unclaimed row of its own: either every
   * candidate row for (sourceId, tokenId) is in `excludeRowIds`, or there
   * are fewer candidates than `occurrenceIndex`. `row` still carries the
   * canonically-first candidate (claimed or not) so a caller like the edit
   * modal can seed its fields from it — callers must not persist edits onto
   * that row directly when `needsOwnRow` is true; a new row is required.
   * Always false for a row-scoped id (`byId` hit) and for a token with no
   * candidates at all (there `row` is `undefined` too — a true "no data").
   */
  needsOwnRow: boolean;
};

/**
 * Resolve a chip's `data-ref-id` (a "token") back to its Reference row,
 * scoped to the source block it was found in. Mirrors `resolveChipRefRow`'s
 * match semantics exactly (row-scoped id match OR legacy target-id match,
 * first row in canonical order when several share a legacy token), but
 * backed by the prebuilt index instead of a linear scan — safe to call on
 * every chip-click without re-scanning the full references array.
 */
export function getReferenceRowFromIndex<T extends ReferenceRowIndexEntry>(
  index: ReferenceRowIndex<T>,
  sourceId: string,
  tokenId: string,
): T | undefined {
  return resolveReferenceRowForOccurrence(index, sourceId, tokenId).row;
}

/**
 * Occurrence-aware variant of `getReferenceRowFromIndex`. For a row-scoped
 * id this is identical (the token already names exactly one row). For a
 * legacy target-id token shared by several rows, it resolves the
 * `occurrenceIndex`-th row among the CANONICALLY ORDERED candidates that are
 * not in `excludeRowIds` — so a block partially migrated by an earlier edit
 * (one occurrence already rewritten to its own row-scoped token) does not
 * hand the remaining legacy occurrence back onto a row a sibling already
 * owns (see `needsOwnRow`).
 */
export function resolveReferenceRowForOccurrence<
  T extends ReferenceRowIndexEntry,
>(
  index: ReferenceRowIndex<T>,
  sourceId: string,
  tokenId: string,
  options: ReferenceRowLookupOptions = {},
): ReferenceRowLookupResult<T> {
  const strippedRowId = stripRowTokenPrefix(tokenId);
  const byIdMatch =
    index.byId.get(tokenId) ??
    (strippedRowId !== null ? index.byId.get(strippedRowId) : undefined);
  if (byIdMatch && byIdMatch.sourceId === sourceId) {
    return { row: byIdMatch, needsOwnRow: false };
  }

  const candidates =
    index.bySourceAndToken.get(sourceTokenKey(sourceId, tokenId)) ?? [];
  if (candidates.length === 0) {
    return { row: undefined, needsOwnRow: false };
  }

  const occurrenceIndex = options.occurrenceIndex ?? 0;
  const excludeRowIds = options.excludeRowIds;
  const unclaimed = excludeRowIds
    ? candidates.filter(row => !excludeRowIds.has(row.id))
    : candidates;

  if (occurrenceIndex < unclaimed.length) {
    return { row: unclaimed[occurrenceIndex], needsOwnRow: false };
  }
  // No unclaimed row for this occurrence — seed from the canonically-first
  // candidate (claimed or not) so a caller can still show the current
  // target/name, but flag that it must not write to that row directly.
  return { row: candidates[0], needsOwnRow: true };
}
